import { useState, useMemo } from 'react'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Activity,
  Database,
  BarChart3,
  Building2,
  AlertTriangle,
  MapPin,
  Users,
  ShieldAlert,
  Waves,
  Train,
  Footprints,
  Store,
} from 'lucide-react'
import {
  CHART_TOOLTIP_PROPS,
  chartAxisProps,
  chartGridProps,
  chartAlertColor,
  chartBarGradient,
  chartSeriesColor,
  chartSparklineBarStyle,
} from '@/config/chartTheme'
import { useAntarcticaDeals, useMarketRates, useModuleDemoEnabled } from '@/hooks/useDemoDataAccess'
import MarketIntelligenceLive from './MarketIntelligenceLive'
import type { ModelComparison } from '@/types'

/* ---------------------------------------------------------------------------
   Mock model data for deals (since the source data doesn't include models)
--------------------------------------------------------------------------- */
const DEAL_MODELS: Record<string, ModelComparison[]> = {
  'ALG-1001': [
    { source: 'Bloomberg', dscr: 1.42, ltv: 0.68, projectedNOI: 5.76, defaultProbability: 0.018, spreadToTreasury: 375 },
    { source: "Moody's", dscr: 1.38, ltv: 0.70, projectedNOI: 5.61, defaultProbability: 0.022, spreadToTreasury: 390 },
    { source: 'S&P', dscr: 1.40, ltv: 0.69, projectedNOI: 5.68, defaultProbability: 0.020, spreadToTreasury: 382 },
    { source: 'Proprietary', dscr: 1.45, ltv: 0.67, projectedNOI: 5.84, defaultProbability: 0.015, spreadToTreasury: 365 },
  ],
  'ALG-1003': [
    { source: 'Bloomberg', dscr: 1.51, ltv: 0.69, projectedNOI: 12.03, defaultProbability: 0.012, spreadToTreasury: 350 },
    { source: "Moody's", dscr: 1.46, ltv: 0.72, projectedNOI: 11.68, defaultProbability: 0.016, spreadToTreasury: 368 },
    { source: 'S&P', dscr: 1.48, ltv: 0.71, projectedNOI: 11.82, defaultProbability: 0.014, spreadToTreasury: 358 },
    { source: 'Proprietary', dscr: 1.53, ltv: 0.68, projectedNOI: 12.18, defaultProbability: 0.010, spreadToTreasury: 340 },
  ],
  'ALG-1004': [
    { source: 'Bloomberg', dscr: 1.47, ltv: 0.69, projectedNOI: 3.64, defaultProbability: 0.016, spreadToTreasury: 360 },
    { source: "Moody's", dscr: 1.42, ltv: 0.72, projectedNOI: 3.52, defaultProbability: 0.021, spreadToTreasury: 378 },
    { source: 'S&P', dscr: 1.44, ltv: 0.70, projectedNOI: 3.58, defaultProbability: 0.019, spreadToTreasury: 370 },
    { source: 'Proprietary', dscr: 1.50, ltv: 0.67, projectedNOI: 3.72, defaultProbability: 0.013, spreadToTreasury: 348 },
  ],
  'ALG-1010': [
    { source: 'Bloomberg', dscr: 1.35, ltv: 0.72, projectedNOI: 6.82, defaultProbability: 0.032, spreadToTreasury: 410 },
    { source: "Moody's", dscr: 1.28, ltv: 0.76, projectedNOI: 6.48, defaultProbability: 0.045, spreadToTreasury: 445 },
    { source: 'S&P', dscr: 1.31, ltv: 0.74, projectedNOI: 6.62, defaultProbability: 0.038, spreadToTreasury: 428 },
    { source: 'Proprietary', dscr: 1.38, ltv: 0.70, projectedNOI: 6.98, defaultProbability: 0.028, spreadToTreasury: 395 },
  ],
}

const DEALS_WITH_MODELS = Object.keys(DEAL_MODELS)

/* ---------------------------------------------------------------------------
   Cap rate grid data
--------------------------------------------------------------------------- */
const CAP_RATE_GRID = [
  { label: 'Multifamily', key: 'multifamilyCapRate' as const, trend: 'down' as const, bpChange: -15 },
  { label: 'Office', key: 'officeCapRate' as const, trend: 'up' as const, bpChange: 25 },
  { label: 'Industrial', key: 'industrialCapRate' as const, trend: 'down' as const, bpChange: -10 },
  { label: 'Retail', key: 'retailCapRate' as const, trend: 'flat' as const, bpChange: 0 },
  { label: 'Hospitality', key: 'hotelCapRate' as const, trend: 'up' as const, bpChange: 30 },
  { label: 'Sr. Living', key: 'seniorLivingCapRate' as const, trend: 'down' as const, bpChange: -5 },
]

/* ---------------------------------------------------------------------------
   Historical rate trend data (12 months)
--------------------------------------------------------------------------- */
const MONTHS = ['Apr 25', 'May 25', 'Jun 25', 'Jul 25', 'Aug 25', 'Sep 25', 'Oct 25', 'Nov 25', 'Dec 25', 'Jan 26', 'Feb 26', 'Mar 26']
const HISTORICAL_RATES = MONTHS.map((month, i) => ({
  month,
  sofr: +(4.05 + Math.sin(i * 0.5) * 0.2 + i * 0.02).toFixed(2),
  ust10y: +(3.85 + Math.cos(i * 0.4) * 0.18 + i * 0.025).toFixed(2),
  cmbsAaa: +(88 + Math.sin(i * 0.6 + 1) * 10 + i * 0.8).toFixed(0),
}))

/* ---------------------------------------------------------------------------
   Neighborhood intelligence data (Item 13)
--------------------------------------------------------------------------- */
const SUBMARKETS = [
  {
    id: 'austin-domain',
    label: 'Austin — The Domain',
    metro: 'Austin, TX',
    assetType: 'Multifamily',
    vacancy: 5.8,
    rentGrowthYoy: 3.2,
    capRate: 4.95,
    popGrowthYoy: 2.1,
    medianHhIncome: 98400,
    crimeIndex: 28,         // lower = safer (0–100)
    floodRisk: 'Low',
    transitScore: 62,
    underConstruction: '1,240 units',
    marketParticipants: ['Greystar', 'Olen Properties', 'JMB Realty'],
  },
  {
    id: 'miami-brickell',
    label: 'Miami — Brickell',
    metro: 'Miami, FL',
    assetType: 'Mixed-Use',
    vacancy: 4.2,
    rentGrowthYoy: 5.8,
    capRate: 4.60,
    popGrowthYoy: 3.4,
    medianHhIncome: 112800,
    crimeIndex: 45,
    floodRisk: 'High',
    transitScore: 78,
    underConstruction: '620 units',
    marketParticipants: ['Ugo Colombo', 'Related Group', 'Terra Group'],
  },
  {
    id: 'dallas-uptown',
    label: 'Dallas — Uptown',
    metro: 'Dallas, TX',
    assetType: 'Multifamily',
    vacancy: 7.4,
    rentGrowthYoy: 1.4,
    capRate: 5.30,
    popGrowthYoy: 1.8,
    medianHhIncome: 88600,
    crimeIndex: 38,
    floodRisk: 'Low',
    transitScore: 55,
    underConstruction: '2,100 units',
    marketParticipants: ['Lincoln Property', 'Behringer Harvard', 'Crow Holdings'],
  },
  {
    id: 'denver-lodo',
    label: 'Denver — LoDo',
    metro: 'Denver, CO',
    assetType: 'Office/Mixed',
    vacancy: 9.1,
    rentGrowthYoy: 0.8,
    capRate: 5.85,
    popGrowthYoy: 1.2,
    medianHhIncome: 91200,
    crimeIndex: 52,
    floodRisk: 'Moderate',
    transitScore: 71,
    underConstruction: '480K SF',
    marketParticipants: ['Brookfield', 'AIMCO', 'Hines'],
  },
  {
    id: 'chicago-fulton',
    label: 'Chicago — Fulton Market',
    metro: 'Chicago, IL',
    assetType: 'Industrial/Office',
    vacancy: 3.8,
    rentGrowthYoy: 6.2,
    capRate: 5.10,
    popGrowthYoy: 0.4,
    medianHhIncome: 104000,
    crimeIndex: 41,
    floodRisk: 'Moderate',
    transitScore: 88,
    underConstruction: '1.2M SF',
    marketParticipants: ['Sterling Bay', 'Fulton Labs', 'Related Midwest'],
  },
]

const FOOT_TRAFFIC_WEEKS = ['W1 Jan', 'W2 Jan', 'W3 Jan', 'W4 Jan', 'W1 Feb', 'W2 Feb', 'W3 Feb', 'W4 Feb', 'W1 Mar', 'W2 Mar', 'W3 Mar', 'W4 Mar']
const RETAIL_PROPERTIES = [
  {
    id: 'rp-001',
    name: 'Mesa Verde Retail (ALG-1008)',
    location: 'Phoenix, AZ',
    type: 'Neighborhood Center',
    weeklyVisits: [4200, 4450, 4180, 4800, 4600, 4750, 4900, 5100, 4950, 5200, 5350, 5600],
    tradeAreaRadiusMi: 3.0,
    tradeAreaPop: 128000,
    medianAge: 38.2,
    avgHhIncome: 82400,
    daytimePopulation: 94000,
    topCompetitors: [
      { name: 'Fry\'s Marketplace', distance: '0.4mi', visits: 8200 },
      { name: 'Total Wine & More', distance: '0.6mi', visits: 3400 },
      { name: 'HomeGoods', distance: '1.1mi', visits: 2900 },
    ],
    visitTrend: 'up',
    visitGrowthYoy: 12.4,
  },
  {
    id: 'rp-002',
    name: 'Eastpoint Retail (P-013)',
    location: 'Atlanta, GA',
    type: 'Strip Center',
    weeklyVisits: [2800, 2750, 2900, 3100, 2950, 3050, 2880, 3200, 3150, 3000, 3180, 3400],
    tradeAreaRadiusMi: 2.0,
    tradeAreaPop: 76000,
    medianAge: 34.8,
    avgHhIncome: 68200,
    daytimePopulation: 52000,
    topCompetitors: [
      { name: 'Kroger', distance: '0.8mi', visits: 5600 },
      { name: 'Dollar Tree', distance: '0.3mi', visits: 2100 },
      { name: 'Planet Fitness', distance: '1.4mi', visits: 1800 },
    ],
    visitTrend: 'up',
    visitGrowthYoy: 8.2,
  },
]

/* ---------------------------------------------------------------------------
   Component
--------------------------------------------------------------------------- */
/**
 * Every constant above is the illustrative dataset. None of it is a feed, so
 * it renders only for a demo account; the live partition gets
 * MarketIntelligenceLive, which shows the rates feed and provider status the
 * platform really has and names every source that is not connected.
 */
export default function MarketIntelligence() {
  const demoEnabled = useModuleDemoEnabled('market-intelligence')
  return demoEnabled ? <MarketIntelligenceDemo /> : <MarketIntelligenceLive />
}

function MarketIntelligenceDemo() {
    const marketRates = useMarketRates('market-intelligence')
  const deals = useAntarcticaDeals('market-intelligence')
  const [selectedDealId, setSelectedDealId] = useState(DEALS_WITH_MODELS[0])
  const [selectedSubmarket, setSelectedSubmarket] = useState(SUBMARKETS[0].id)
  const [selectedRetailProperty, setSelectedRetailProperty] = useState(RETAIL_PROPERTIES[0].id)
  const [intelPane, setIntelPane] = useState<'neighborhood' | 'foot-traffic' | 'rates'>('neighborhood')

  const selectedDeal = useMemo(
    () => deals.find((d) => d.id === selectedDealId),
    [selectedDealId],
  )
  const models = DEAL_MODELS[selectedDealId] ?? []

  // Model dispersion chart data
  const dispersionData = useMemo(() => {
    if (!models.length) return []
    return [
      {
        metric: 'DSCR',
        min: Math.min(...models.map((m) => m.dscr)),
        max: Math.max(...models.map((m) => m.dscr)),
        range: Math.max(...models.map((m) => m.dscr)) - Math.min(...models.map((m) => m.dscr)),
        avg: +(models.reduce((s, m) => s + m.dscr, 0) / models.length).toFixed(2),
      },
      {
        metric: 'LTV',
        min: +(Math.min(...models.map((m) => m.ltv)) * 100).toFixed(1),
        max: +(Math.max(...models.map((m) => m.ltv)) * 100).toFixed(1),
        range: +((Math.max(...models.map((m) => m.ltv)) - Math.min(...models.map((m) => m.ltv))) * 100).toFixed(1),
        avg: +((models.reduce((s, m) => s + m.ltv, 0) / models.length) * 100).toFixed(1),
      },
    ]
  }, [models])

  const dscrSpread = dispersionData.find((d) => d.metric === 'DSCR')?.range ?? 0
  const showWarning = dscrSpread > 0.08

  const TrendIcon = ({ trend }: { trend: 'up' | 'down' | 'flat' }) => {
    if (trend === 'up') return <TrendingUp className="h-3 w-3 text-red-400" />
    if (trend === 'down') return <TrendingDown className="h-3 w-3 text-emerald-400" />
    return <Minus className="h-3 w-3 text-stone-500" />
  }

  return (
    <div className="page-fill gap-2 p-3">
      {/* ── RATE STRIP ─────────────────────────────────────────────── */}
      <div className="market-strip rounded-lg px-4 py-2 flex items-center gap-6 overflow-x-auto">
        {[
          { label: 'SOFR', value: `${marketRates.sofr}%`, color: 'text-amber-300' },
          { label: '10Y UST', value: `${marketRates.ust10y}%`, color: 'text-amber-300' },
          { label: 'AAA', value: `+${marketRates.cmbsAaaSpread}bp`, color: 'text-emerald-400' },
          { label: 'BBB', value: `+${marketRates.cmbsBbbSpread}bp`, color: 'text-red-400' },
        ].map((r) => (
          <div key={r.label} className="market-chip rounded px-3 py-1.5 flex items-center gap-2 whitespace-nowrap">
            <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-stone-500">{r.label}</span>
            <span className={`text-sm font-semibold ${r.color}`}>{r.value}</span>
          </div>
        ))}
        <div className="ml-auto flex items-center gap-1.5 text-stone-600">
          <Activity className="h-3 w-3" />
          <span className="font-mono text-[9px] uppercase tracking-[0.12em]">Sample data</span>
        </div>
      </div>

      {/* ── CAP RATE GRID ──────────────────────────────────────────── */}
      <div className="rounded-lg border border-amber-400/10 bg-black/30 p-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500 mb-3">
          Market Rates &amp; Benchmarks
        </div>
        <div className="grid grid-cols-6 gap-2">
          {CAP_RATE_GRID.map((item) => (
            <div key={item.label} className="metric-cell text-center">
              <div className="font-mono text-[9px] uppercase tracking-[0.12em] text-stone-500 mb-1">{item.label}</div>
              <div className="text-lg font-semibold text-stone-200">{marketRates[item.key].toFixed(2)}%</div>
              <div className="flex items-center justify-center gap-1 mt-1">
                <TrendIcon trend={item.trend} />
                <span
                  className={`text-[11px] font-mono ${
                    item.trend === 'up' ? 'text-red-400' : item.trend === 'down' ? 'text-emerald-400' : 'text-stone-500'
                  }`}
                >
                  {item.bpChange > 0 ? `+${item.bpChange}bp` : item.bpChange < 0 ? `${item.bpChange}bp` : 'flat'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── MIDDLE ROW: MODEL COMPARISON + MARKET DATA FEEDS ─────── */}
      <div className="grid min-h-0 flex-1 grid-cols-2 gap-2 overflow-hidden">
        {/* Model Comparison */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-amber-400/10 bg-black/30">
          <div className="flex items-center justify-between border-b border-amber-400/10 px-3 py-2">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-3.5 w-3.5 text-amber-400/60" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500">Model Comparison</span>
            </div>
            <select
              className="input text-xs py-1 px-2 bg-black/50"
              value={selectedDealId}
              onChange={(e) => setSelectedDealId(e.target.value)}
            >
              {DEALS_WITH_MODELS.map((id) => {
                const deal = deals.find((d) => d.id === id)
                return (
                  <option key={id} value={id}>
                    {id} — {deal?.property ?? 'Unknown'}
                  </option>
                )
              })}
            </select>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-3 scrollbar-thin">
            {selectedDeal && (
              <div className="text-xs text-stone-400 mb-2">
                {selectedDeal.property} &middot; {selectedDeal.type} &middot; ${selectedDeal.loanAmount}M
              </div>
            )}

            {/* Model table */}
            <table className="dense-table mb-4">
              <thead>
                <tr>
                  <th>Source</th>
                  <th className="text-right">DSCR</th>
                  <th className="text-right">LTV</th>
                  <th className="text-right">Proj NOI</th>
                  <th className="text-right">Default%</th>
                  <th className="text-right">Spread</th>
                </tr>
              </thead>
              <tbody>
                {models.map((m) => (
                  <tr key={m.source}>
                    <td className="font-medium text-stone-300">{m.source}</td>
                    <td className="text-right font-mono">{m.dscr.toFixed(2)}x</td>
                    <td className="text-right font-mono">{(m.ltv * 100).toFixed(0)}%</td>
                    <td className="text-right font-mono">${m.projectedNOI.toFixed(2)}M</td>
                    <td className="text-right font-mono">
                      <span className={m.defaultProbability! > 0.03 ? 'text-red-400' : 'text-stone-300'}>
                        {(m.defaultProbability! * 100).toFixed(1)}%
                      </span>
                    </td>
                    <td className="text-right font-mono">{m.spreadToTreasury}bp</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Model Dispersion */}
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-stone-500">Model Dispersion</span>
              {showWarning && (
                <span className="flex items-center gap-1 text-amber-400 text-[10px]">
                  <AlertTriangle className="h-3 w-3" />
                  Significant divergence
                </span>
              )}
            </div>
            <div className="h-28">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dispersionData} layout="vertical" margin={{ top: 0, right: 20, bottom: 0, left: 40 }}>
                  <CartesianGrid {...chartGridProps(false)} />
                  <XAxis type="number" {...chartAxisProps()} />
                  <YAxis dataKey="metric" type="category" {...chartAxisProps()} width={36} />
                  <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(value: number, name: string) => [value.toFixed(2), name]} />
                  <Bar dataKey="min" stackId="a" fill="transparent" />
                  <Bar dataKey="range" stackId="a" radius={[0, 4, 4, 0]}>
                    {dispersionData.map((_, i) => (
                      <Cell key={i} fill={showWarning && i === 0 ? chartAlertColor() : chartSeriesColor(i)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Market Data Feeds */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-amber-400/10 bg-black/30">
          <div className="px-3 py-2 border-b border-amber-400/10 flex items-center gap-2">
            <Database className="h-3.5 w-3.5 text-amber-400/60" />
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500">Market Data Feeds</span>
            <span className="ml-auto text-[9px] text-stone-500">Sample data · no feed</span>
          </div>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 scrollbar-thin">
            {/* CoStar */}
            <FeedPanel
              title="CoStar Integration"
              items={[
                { label: 'Vacancy Rate', value: '6.2%' },
                { label: 'Asking Rent', value: '$32.50/SF' },
                { label: '12mo Net Absorption', value: '1.2M SF' },
                { label: 'Under Construction', value: '890K SF' },
              ]}
            />

            {/* REIS */}
            <FeedPanel
              title="REIS Data"
              items={[
                { label: 'Effective Rent', value: '$31.80/SF' },
                { label: 'Rent Growth', value: '2.8%' },
                { label: 'Vacancy Forecast', value: '5.8%' },
              ]}
            />

            {/* Cushman & Wakefield */}
            <FeedPanel
              title="Cushman & Wakefield"
              items={[
                { label: 'Cap Rate (Multifamily)', value: '5.15%' },
                { label: 'Cap Rate (Office)', value: '6.80%' },
                { label: 'Cap Rate (Industrial)', value: '5.45%' },
                { label: 'Investment Volume YTD', value: '$142.3B' },
                { label: 'Avg Deal Size', value: '$38.5M' },
              ]}
            />

            {/* Trepp CMBS */}
            <FeedPanel
              title="Trepp CMBS"
              items={[
                { label: 'Delinquency Rate', value: '4.2%' },
                { label: 'Special Servicing', value: '5.1%' },
                { label: 'New Issuance MTD', value: '$12.4B' },
                { label: 'YTD Volume', value: '$58.2B' },
              ]}
            />
          </div>
        </div>
      </div>

      {/* ── LOWER PANE ─────────────────────────────────────────────── */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-amber-400/10 bg-black/30">
        <div className="flex flex-shrink-0 items-center gap-1 border-b border-amber-400/10 px-2">
          {([
            { id: 'neighborhood' as const, label: 'Neighborhood Intel', icon: <MapPin className="h-3 w-3" /> },
            { id: 'foot-traffic' as const, label: 'Foot Traffic', icon: <Footprints className="h-3 w-3" /> },
            { id: 'rates' as const, label: 'Rate Trends', icon: <Activity className="h-3 w-3" /> },
          ]).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setIntelPane(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium border-b-2 transition-all ${
                intelPane === tab.id
                  ? 'border-amber-400 text-amber-300'
                  : 'border-transparent text-stone-500 hover:text-stone-300'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
      {intelPane === 'neighborhood' && (
      <div>
        <div className="px-3 py-2 border-b border-amber-400/10 flex items-center gap-2">
          <MapPin className="h-3.5 w-3.5 text-amber-400/60" />
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500">Neighborhood Intelligence</span>
          <select
            className="ml-auto input text-xs py-1 px-2 bg-black/50"
            value={selectedSubmarket}
            onChange={e => setSelectedSubmarket(e.target.value)}
          >
            {SUBMARKETS.map(s => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>
        {(() => {
          const sub = SUBMARKETS.find(s => s.id === selectedSubmarket)!
          const crimeColor = sub.crimeIndex < 30 ? 'text-emerald-400' : sub.crimeIndex < 55 ? 'text-amber-400' : 'text-red-400'
          const floodColor = sub.floodRisk === 'Low' ? 'text-emerald-400' : sub.floodRisk === 'Moderate' ? 'text-amber-400' : 'text-red-400'
          const transitColor = sub.transitScore >= 70 ? 'text-emerald-400' : sub.transitScore >= 50 ? 'text-amber-400' : 'text-stone-500'
          const vacancyColor = sub.vacancy < 5 ? 'text-emerald-400' : sub.vacancy < 8 ? 'text-amber-400' : 'text-red-400'
          const rentColor = sub.rentGrowthYoy > 3 ? 'text-emerald-400' : sub.rentGrowthYoy > 1 ? 'text-amber-400' : 'text-stone-400'
          return (
            <div className="p-3 space-y-3">
              <div className="grid grid-cols-4 gap-2 lg:grid-cols-8">
                {[
                  { label: 'Vacancy Rate', value: `${sub.vacancy.toFixed(1)}%`, color: vacancyColor, icon: <Building2 className="h-3 w-3" /> },
                  { label: 'Rent Growth YoY', value: `+${sub.rentGrowthYoy.toFixed(1)}%`, color: rentColor, icon: <TrendingUp className="h-3 w-3" /> },
                  { label: 'Cap Rate', value: `${sub.capRate.toFixed(2)}%`, color: 'text-stone-200', icon: <Activity className="h-3 w-3" /> },
                  { label: 'Pop. Growth YoY', value: `+${sub.popGrowthYoy.toFixed(1)}%`, color: 'text-stone-200', icon: <Users className="h-3 w-3" /> },
                  { label: 'Median HH Income', value: `$${(sub.medianHhIncome / 1000).toFixed(0)}K`, color: 'text-stone-200', icon: <Users className="h-3 w-3" /> },
                  { label: 'Crime Index', value: `${sub.crimeIndex}/100`, color: crimeColor, icon: <ShieldAlert className="h-3 w-3" /> },
                  { label: 'Flood Risk', value: sub.floodRisk, color: floodColor, icon: <Waves className="h-3 w-3" /> },
                  { label: 'Transit Score', value: `${sub.transitScore}/100`, color: transitColor, icon: <Train className="h-3 w-3" /> },
                ].map(m => (
                  <div key={m.label} className="metric-cell text-center">
                    <div className="flex items-center justify-center gap-1 text-stone-600 mb-1">{m.icon}<span className="font-mono text-[9px] uppercase tracking-[0.1em]">{m.label}</span></div>
                    <div className={`text-sm font-semibold ${m.color}`}>{m.value}</div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md border border-amber-400/10 bg-black/20 px-3 py-2.5">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-stone-600 mb-1.5">Under Construction</div>
                  <div className="text-sm font-semibold text-stone-200">{sub.underConstruction}</div>
                  <div className="text-[10px] text-stone-500 mt-0.5">{sub.metro} · {sub.assetType}</div>
                </div>
                <div className="rounded-md border border-amber-400/10 bg-black/20 px-3 py-2.5">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-stone-600 mb-1.5">Key Market Participants</div>
                  <div className="flex flex-wrap gap-1">
                    {sub.marketParticipants.map(p => (
                      <span key={p} className="px-1.5 py-0.5 rounded border border-amber-400/15 bg-amber-400/[0.05] text-[10px] text-amber-300/80">{p}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )
        })()}
      </div>
      )}

      {intelPane === 'foot-traffic' && (
      <div>
        <div className="px-3 py-2 border-b border-amber-400/10 flex items-center gap-2">
          <Footprints className="h-3.5 w-3.5 text-amber-400/60" />
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500">Foot Traffic & Alternative Data</span>
          <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] border border-blue-400/30 bg-blue-400/[0.06] text-blue-300 font-mono">Placer.ai-style</span>
          <select
            className="ml-auto input text-xs py-1 px-2 bg-black/50"
            value={selectedRetailProperty}
            onChange={e => setSelectedRetailProperty(e.target.value)}
          >
            {RETAIL_PROPERTIES.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
        {(() => {
          const prop = RETAIL_PROPERTIES.find(r => r.id === selectedRetailProperty)!
          const maxVisits = Math.max(...prop.weeklyVisits)
          const trendColor = prop.visitTrend === 'up' ? 'text-emerald-400' : 'text-red-400'
          return (
            <div className="p-3 space-y-3">
              <div className="grid grid-cols-3 gap-3 lg:grid-cols-6">
                {[
                  { label: 'Visit Growth YoY', value: `+${prop.visitGrowthYoy.toFixed(1)}%`, color: trendColor },
                  { label: 'Trade Area Radius', value: `${prop.tradeAreaRadiusMi}mi` , color: 'text-stone-200' },
                  { label: 'Trade Area Pop', value: `${(prop.tradeAreaPop / 1000).toFixed(0)}K`, color: 'text-stone-200' },
                  { label: 'Median Age', value: String(prop.medianAge), color: 'text-stone-200' },
                  { label: 'Avg HH Income', value: `$${(prop.avgHhIncome / 1000).toFixed(0)}K`, color: 'text-stone-200' },
                  { label: 'Daytime Pop', value: `${(prop.daytimePopulation / 1000).toFixed(0)}K`, color: 'text-stone-200' },
                ].map(m => (
                  <div key={m.label} className="metric-cell text-center">
                    <div className="font-mono text-[9px] uppercase tracking-[0.1em] text-stone-600 mb-1">{m.label}</div>
                    <div className={`text-sm font-semibold ${m.color}`}>{m.value}</div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {/* Weekly visit sparkline */}
                <div className="rounded-md border border-amber-400/10 bg-black/20 px-3 py-2.5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-stone-600">Weekly Visit Volume (12-Week)</div>
                    <div className="flex items-center gap-1 text-[10px]">
                      <TrendingUp className="h-3 w-3 text-emerald-400" />
                      <span className={trendColor}>{prop.visitGrowthYoy.toFixed(1)}% YoY</span>
                    </div>
                  </div>
                  <div className="flex items-end gap-1 h-16">
                    {prop.weeklyVisits.map((v, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                        <div
                          className="chart-viz-bar chart-viz-bar--rounded-sm w-full transition-all"
                          style={{
                            height: `${(v / maxVisits) * 100}%`,
                            ...chartSparklineBarStyle(i, prop.weeklyVisits.length),
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-1.5 text-[9px] text-stone-600">
                    <span>{FOOT_TRAFFIC_WEEKS[0]}</span>
                    <span>{FOOT_TRAFFIC_WEEKS[FOOT_TRAFFIC_WEEKS.length - 1]}</span>
                  </div>
                </div>
                {/* Competitive set */}
                <div className="rounded-md border border-amber-400/10 bg-black/20 px-3 py-2.5">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-stone-600 mb-2">
                    <Store className="h-3 w-3 inline mr-1.5 text-amber-400/50" />Competitive Set (Nearby Anchors)
                  </div>
                  <div className="space-y-2">
                    {prop.topCompetitors.map(c => {
                      const sharePct = (c.visits / (c.visits + prop.weeklyVisits[prop.weeklyVisits.length - 1])) * 100
                      return (
                        <div key={c.name} className="space-y-0.5">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-stone-300">{c.name}</span>
                            <div className="flex items-center gap-2 text-[10px] font-mono text-stone-500">
                              <span>{c.distance}</span>
                              <span className="text-stone-400">{c.visits.toLocaleString()} vis/wk</span>
                            </div>
                          </div>
                          <div className="chart-viz-track chart-viz-bar--rounded h-1.5">
                            <div
                              className="chart-viz-bar chart-viz-bar--rounded h-full"
                              style={{ width: `${sharePct}%`, background: chartBarGradient(1, 2) }}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div className="mt-2 text-[10px] text-stone-600">Subject property draws {prop.weeklyVisits[prop.weeklyVisits.length - 1].toLocaleString()} visits/wk</div>
                </div>
              </div>
            </div>
          )
        })()}
      </div>
      )}

      {intelPane === 'rates' && (
      <div>
        <div className="px-3 py-2 border-b border-amber-400/10 flex items-center gap-2">
          <Activity className="h-3.5 w-3.5 text-amber-400/60" />
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500">Historical Rate Trends</span>
          <span className="text-[10px] text-stone-600 ml-auto">12-Month Lookback</span>
        </div>
        <div className="p-3 h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={HISTORICAL_RATES} margin={{ top: 8, right: 24, bottom: 0, left: 0 }}>
              <CartesianGrid {...chartGridProps()} />
              <XAxis dataKey="month" {...chartAxisProps()} />
              <YAxis
                yAxisId="pct"
                {...chartAxisProps()}
                domain={[3.6, 4.8]}
                tickFormatter={(v: number) => `${v}%`}
              />
              <YAxis
                yAxisId="bp"
                orientation="right"
                {...chartAxisProps()}
                domain={[70, 120]}
                tickFormatter={(v: number) => `${v}bp`}
              />
              <Tooltip {...CHART_TOOLTIP_PROPS} />
              <Legend
                wrapperStyle={{ fontSize: 10, paddingTop: 4 }}
                formatter={(value: string) => <span className="text-stone-400 text-[10px]">{value}</span>}
              />
              <Line yAxisId="pct" type="monotone" dataKey="sofr" name="SOFR" stroke={chartSeriesColor(0)} strokeWidth={1.5} dot={false} />
              <Line yAxisId="pct" type="monotone" dataKey="ust10y" name="10Y UST" stroke={chartSeriesColor(1)} strokeWidth={1.5} dot={false} />
              <Line yAxisId="bp" type="monotone" dataKey="cmbsAaa" name="CMBS AAA Spread" stroke={chartSeriesColor(2)} strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      )}
        </div>
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------------------
   Feed sub-panel component
--------------------------------------------------------------------------- */
function FeedPanel({ title, items }: { title: string; items: { label: string; value: string }[] }) {
  return (
    <div className="rounded-md border border-amber-400/10 bg-black/20">
      <div className="px-2.5 py-1.5 border-b border-amber-400/10">
        <div className="flex items-center gap-1.5">
          <Building2 className="h-3 w-3 text-amber-400/40" />
          <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-stone-500">{title}</span>
        </div>
      </div>
      <div className="p-2.5 grid grid-cols-2 gap-x-4 gap-y-1.5">
        {items.map((item) => (
          <div key={item.label} className="flex items-center justify-between">
            <span className="text-[11px] text-stone-500">{item.label}</span>
            <span className="text-[12px] font-mono text-stone-200 font-medium">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
